import type {
  BudgetSheetState,
  DocumentInsight,
  DraftSubprojectPayload,
  MetricComputationView,
  MetricLegajo,
  MetricSheetState,
  ProjectAutofillSkillMatchKind,
  TechnicalSpecificationState
} from '../../services/projectAutofillDraftService.ts'
import {
  attachBudgetBindingToState,
  serializeBudgetSheetForDirtySnapshot
} from './budgetSheetStateUtils.ts'
import {
  attachMetricBindingToState,
  serializeMetricSheetForDirtySnapshot
} from './metricSheetStateUtils.ts'
import {
  attachTechnicalSpecBindingToState
} from './technicalSpecStateUtils.ts'
import { serializeTechnicalSpecForDirtySnapshot } from './technicalSpecStateUtils.ts'
import type {
  MetricEligibility,
  QuotationEligibility,
  TechnicalSpecEligibility
} from './projectAutofillWorkflow.ts'
import {
  extractRubroMetricWorkflowFields,
  parseMetricEligibility,
  parseQuotationEligibility,
  rubroMetricReadyFromRow,
  rubroTechnicalSpecLegajoInPdf
} from './projectAutofillWorkflow.ts'
import { parseDocumentInsight } from '../../services/projectAutofillDraftService.ts'
import { isProjectAutofillAgentId } from '../../constants/agentNames.ts'
import { toHtmlDateInputValue } from '../toHtmlDateInputValue.ts'

export type PublicationReadiness = {
  phase?: string
  projectAssignReady?: boolean
  subprojectPayloadReady?: boolean
  readyForCommitSequence?: boolean
  missingForProjectAssign?: string[]
  missingForSubprojectCreate?: string[]
  dateConstraintOk?: boolean
}

export type DraftValidationShape = {
  blockingIssues?: string[]
  warnings?: string[]
  publicationReadiness?: PublicationReadiness
}

export type DocumentExtractionSource = 'native' | 'ocr' | 'none' | string

export type DocumentExtractionMeta = {
  source?: DocumentExtractionSource
  needsOcr?: boolean
  truncated?: boolean
  originalCharCount?: number
  pageCount?: number
  parseError?: string
  ocrError?: string
}

export type DocumentationSlots = {
  budget?: number[]
  metricComputation?: number[]
  technicalSpecifications?: number[]
  planos?: number[]
}

export type DocumentationFileIds = {
  budget?: string
  metricComputation?: string
  technicalSpecifications?: string
  planos?: string
}

export type DocumentationSlotSourceEntry = {
  fileId: string
  pages: number[]
}

export type DocumentationSlotSources = Partial<
  Record<keyof DocumentationSlots, DocumentationSlotSourceEntry[]>
>

export type DraftMetadataFileEntry = {
  id?: string
  name?: string
  originalName?: string
  storageUrl?: string
  mimeType?: string
  tag?: string
  pageCount?: number
}

export function draftMetadataFileLabel(f: DraftMetadataFileEntry): string {
  const n = f.name || f.originalName
  if (n && String(n).trim()) return String(n).trim()
  const id = f.id != null ? String(f.id) : ''
  return id.length > 8 ? `${id.slice(0, 6)}…` : id || 'Archivo'
}

export const DOCUMENTATION_SLOT_FILE_ACCEPT =
  '.pdf,.csv,.xlsx,.docx,.mp4,.jpg,.jpeg,.png,.gif,.webp,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/mp4,image/jpeg,image/png,image/gif,image/webp'

export const PRECARGA_MANUAL_DOCUMENTATION_FILE_ACCEPT =
  '.pdf,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.webp,application/pdf,image/*,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'

export function isPrecargaManualDocumentationFile(file: File): boolean {
  const t = (file.type || '').trim().toLowerCase()
  if (t.includes('pdf')) return true
  if (t.startsWith('image/')) return true
  if (
    t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    t === 'application/vnd.ms-excel'
  ) {
    return true
  }
  const n = (file.name || '').trim().toLowerCase()
  if (n.endsWith('.pdf')) return true
  if (/\.(jpe?g|png|gif|webp)$/.test(n)) return true
  if (n.endsWith('.xlsx') || n.endsWith('.xls')) return true
  return false
}

function precargaManualFileListKey(f: File): string {
  return `${f.name || ''}\u0000${f.size}\u0000${f.lastModified}`
}

export function collectUniquePrecargaManualFiles(incoming: File[]): File[] {
  const seen = new Set<string>()
  const out: File[] = []
  for (const f of incoming) {
    if (!isPrecargaManualDocumentationFile(f)) continue
    const k = precargaManualFileListKey(f)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(f)
  }
  return out
}

export type DocumentationPreviewAssignment = {
  tempId?: string
  label?: string
  matchedSkillName?: string
  selectedSkillId?: number
  skillMatchKind?: ProjectAutofillSkillMatchKind
  sourcePages?: number[]
  documentationSlots?: DocumentationSlots
  sourceFileId?: string
  documentationSlotSources?: DocumentationSlotSources
}

export type DocumentationPreviewMeta = {
  assignments?: DocumentationPreviewAssignment[]
  pageCount?: number
  primaryStorageUrl?: string
  primaryFileId?: string
  status?: string
}

export const DOCUMENTATION_SLOT_KEYS: (keyof DocumentationSlots)[] = [
  'budget',
  'metricComputation',
  'technicalSpecifications',
  'planos'
]

export const DOCUMENTATION_SLOT_DEFINITIONS: {
  key: keyof DocumentationSlots
  label: string
  chipLabel: string
}[] = [
  { key: 'budget', label: 'Planilla de cotización', chipLabel: 'Presupuesto' },
  { key: 'metricComputation', label: 'Cómputo métrico', chipLabel: 'Cómputo' },
  { key: 'technicalSpecifications', label: 'Especificaciones técnicas', chipLabel: 'Especificaciones' },
  { key: 'planos', label: 'Planos', chipLabel: 'Planos' }
]

export type DocumentationPageRow = {
  page: number
  types: (keyof DocumentationSlots)[]
}

export type DocumentationDisplayRow = {
  fileId: string
  page: number
  types: (keyof DocumentationSlots)[]
}

export const buildDocumentationPageRows = (
  slots: DocumentationSlots,
  fileIds: DocumentationFileIds
): DocumentationPageRow[] => {
  const map = new Map<number, Set<keyof DocumentationSlots>>()
  for (const key of DOCUMENTATION_SLOT_KEYS) {
    if (fileIds[key]) continue
    const pages = slots[key]
    if (!pages?.length) continue
    for (const p of pages) {
      if (!Number.isFinite(p) || p < 1) continue
      if (!map.has(p)) map.set(p, new Set())
      map.get(p)!.add(key)
    }
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([page, typeSet]) => ({
      page,
      types: DOCUMENTATION_SLOT_KEYS.filter((k) => typeSet.has(k))
    }))
}

export const normalizeDocumentationSlotSources = (raw: unknown): DocumentationSlotSources => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const o = raw as Record<string, unknown>
  const out: DocumentationSlotSources = {}
  for (const k of DOCUMENTATION_SLOT_KEYS) {
    const arr = o[k as string]
    if (!Array.isArray(arr)) continue
    const entries: DocumentationSlotSourceEntry[] = []
    for (const item of arr) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue
      const it = item as Record<string, unknown>
      const fid = it.fileId != null ? String(it.fileId).trim() : ''
      const pages = normalizePageList(it.pages)
      if (!fid || !pages?.length) continue
      entries.push({ fileId: fid, pages: [...pages] })
    }
    if (entries.length) out[k] = entries
  }
  return out
}

export const documentationSlotSourcesHaveData = (src: DocumentationSlotSources): boolean =>
  DOCUMENTATION_SLOT_KEYS.some((k) => {
    const list = src[k]
    return Array.isArray(list) && list.length > 0
  })

export const documentationSlotUnionPages = (
  sources: DocumentationSlotSources,
  slot: keyof DocumentationSlots
): number[] | undefined => {
  const list = sources[slot]
  if (!list?.length) return undefined
  const set = new Set<number>()
  for (const e of list) {
    for (const p of e.pages) {
      if (typeof p === 'number' && Number.isFinite(p) && p >= 1) set.add(p)
    }
  }
  return normalizePageList(Array.from(set))
}

export const buildDocumentationDisplayRows = (
  slots: DocumentationSlots,
  fileIds: DocumentationFileIds,
  slotSources: DocumentationSlotSources | undefined
): DocumentationDisplayRow[] => {
  const src = slotSources && documentationSlotSourcesHaveData(slotSources) ? slotSources : undefined
  if (!src) {
    return buildDocumentationPageRows(slots, fileIds).map((r) => ({
      fileId: '',
      page: r.page,
      types: r.types
    }))
  }
  const map = new Map<string, Set<keyof DocumentationSlots>>()
  const rowKey = (fileId: string, page: number) => `${fileId}\u0000${page}`
  for (const key of DOCUMENTATION_SLOT_KEYS) {
    if (fileIds[key]) continue
    const entries = src[key]
    if (entries?.length) {
      for (const ent of entries) {
        const fid = String(ent.fileId || '').trim()
        if (!fid) continue
        for (const p of ent.pages) {
          if (!Number.isFinite(p) || p < 1) continue
          const rk = rowKey(fid, p)
          if (!map.has(rk)) map.set(rk, new Set())
          map.get(rk)!.add(key)
        }
      }
      continue
    }
    const pages = slots[key]
    if (!pages?.length) continue
    for (const p of pages) {
      if (!Number.isFinite(p) || p < 1) continue
      const rk = rowKey('', p)
      if (!map.has(rk)) map.set(rk, new Set())
      map.get(rk)!.add(key)
    }
  }
  return Array.from(map.entries())
    .sort((a, b) => {
      const [fa, pa] = a[0].split('\u0000')
      const [fb, pb] = b[0].split('\u0000')
      const c = fa.localeCompare(fb)
      if (c !== 0) return c
      return Number(pa) - Number(pb)
    })
    .map(([rk, typeSet]) => {
      const [fileId, pageStr] = rk.split('\u0000')
      return {
        fileId,
        page: Number(pageStr),
        types: DOCUMENTATION_SLOT_KEYS.filter((k) => typeSet.has(k))
      }
    })
}

export const normalizePageList = (raw: unknown): number[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const nums = raw
    .map((n) => (typeof n === 'number' ? n : Number(n)))
    .filter((n) => Number.isFinite(n) && n >= 1)
  const unique = Array.from(new Set(nums)).sort((a, b) => a - b)
  return unique.length ? unique : undefined
}

export const normalizeDocumentationSlots = (raw: unknown): DocumentationSlots => {
  if (!raw || typeof raw !== 'object') return {}
  const o = raw as Record<string, unknown>
  return {
    budget: normalizePageList(o.budget),
    metricComputation: normalizePageList(o.metricComputation),
    technicalSpecifications: normalizePageList(o.technicalSpecifications),
    planos: normalizePageList(o.planos)
  }
}

export const normalizeDocumentationFileIds = (raw: unknown): DocumentationFileIds => {
  if (!raw || typeof raw !== 'object') return {}
  const o = raw as Record<string, unknown>
  const id = (v: unknown) => {
    const s = v != null ? String(v).trim() : ''
    return s.length > 0 ? s : undefined
  }
  return {
    budget: id(o.budget),
    metricComputation: id(o.metricComputation),
    technicalSpecifications: id(o.technicalSpecifications),
    planos: id(o.planos)
  }
}

export const documentationSlotsHavePages = (slots: DocumentationSlots): boolean =>
  DOCUMENTATION_SLOT_KEYS.some((k) => Array.isArray(slots[k]) && (slots[k] as number[]).length > 0)

export const isFullPageSelection = (pages: number[] | undefined, maxPage: number): boolean => {
  if (!Number.isFinite(maxPage) || maxPage < 1) return false
  const norm = normalizePageList(pages)
  if (!norm || norm.length !== maxPage) return false
  for (let i = 0; i < maxPage; i++) {
    if (norm[i] !== i + 1) return false
  }
  return true
}

export type RubroDocumentationResolved = {
  slots: DocumentationSlots
  sourcePages?: number[]
  sourceFileId?: string
  documentationSlotSources?: DocumentationSlotSources
}

export const resolveRubroDocumentation = (
  tempId: string,
  documentationPreview: DocumentationPreviewMeta | null | undefined,
  draftSubprojects:
    | Array<{
        tempId?: string
        documentationSlots?: DocumentationSlots
        sourcePages?: number[]
        sourceFileId?: string
        documentationSlotSources?: DocumentationSlotSources
      }>
    | undefined
): RubroDocumentationResolved => {
  const subs = Array.isArray(draftSubprojects) ? draftSubprojects : []
  const sp = subs.find((s) => String(s?.tempId ?? '') === String(tempId))
  if (sp) {
    return {
      slots: normalizeDocumentationSlots(sp.documentationSlots),
      sourcePages: Array.isArray(sp.sourcePages) ? normalizePageList(sp.sourcePages) : undefined,
      sourceFileId: sp.sourceFileId != null ? String(sp.sourceFileId).trim() || undefined : undefined,
      documentationSlotSources: documentationSlotSourcesHaveData(
        normalizeDocumentationSlotSources(sp.documentationSlotSources)
      )
        ? normalizeDocumentationSlotSources(sp.documentationSlotSources)
        : undefined
    }
  }
  const assignments = documentationPreview?.assignments
  if (Array.isArray(assignments) && assignments.length > 0) {
    const a = assignments.find((x) => String(x?.tempId ?? '') === String(tempId))
    if (a) {
      const src = normalizeDocumentationSlotSources(a.documentationSlotSources)
      return {
        slots: normalizeDocumentationSlots(a.documentationSlots),
        sourcePages: Array.isArray(a.sourcePages) ? normalizePageList(a.sourcePages) : undefined,
        sourceFileId: a.sourceFileId != null ? String(a.sourceFileId).trim() || undefined : undefined,
        documentationSlotSources: documentationSlotSourcesHaveData(src) ? src : undefined
      }
    }
  }
  return { slots: {} }
}

export function getEffectiveDocumentationPageCount(
  documentationPreview: DocumentationPreviewMeta | null | undefined,
  draftMetadata:
    | {
        pageCount?: number
      }
    | null
    | undefined
): number | null {
  const fromPreview = documentationPreview?.pageCount
  if (typeof fromPreview === 'number' && Number.isFinite(fromPreview) && fromPreview >= 1) {
    return fromPreview
  }
  const fromMeta = draftMetadata?.pageCount
  if (typeof fromMeta === 'number' && Number.isFinite(fromMeta) && fromMeta >= 1) {
    return fromMeta
  }
  return null
}

export function extractDraftMetadataFilesFromResponse(res: unknown): DraftMetadataFileEntry[] | null {
  if (!res || typeof res !== 'object') return null
  const r = res as Record<string, unknown>
  const fromContainer = (obj: Record<string, unknown> | null): DraftMetadataFileEntry[] | null => {
    if (!obj) return null
    const meta = obj.metadata && typeof obj.metadata === 'object' ? (obj.metadata as Record<string, unknown>) : null
    const files = meta?.files
    return Array.isArray(files) ? (files as DraftMetadataFileEntry[]) : null
  }
  const rootFiles = fromContainer(r as Record<string, unknown>)
  if (rootFiles) return rootFiles
  const data = r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const direct = fromContainer(data as Record<string, unknown>)
  if (direct) return direct
  const draft = data.draft && typeof data.draft === 'object' ? (data.draft as Record<string, unknown>) : null
  return fromContainer(draft)
}

export function pickNewFileIdAfterUpload(
  previous: DraftMetadataFileEntry[] | undefined,
  next: DraftMetadataFileEntry[] | undefined
): string | undefined {
  const prev = new Set((previous || []).map((f) => (f?.id != null ? String(f.id) : '')).filter(Boolean))
  for (const f of next || []) {
    const id = f?.id != null ? String(f.id) : ''
    if (id && !prev.has(id)) return id
  }
  return undefined
}

export type ProjectAutofillPayload = {
  actionId?: string
  activeAgent?: string
  draftId?: string
  nextAction?: string
  documentInsight?: DocumentInsight | null
  collectedData?: { requiredFields?: string[]; draftId?: string; draftStatus?: string; nextAction?: string }
  validation?: DraftValidationShape
  draft?: {
    id?: string
    status?: string
    metadata?: {
      nameSource?: string
      endDateSource?: string
      descriptionSource?: string
      discardedPastProjectDates?: boolean
      documentExtraction?: DocumentExtractionMeta
      documentationPreview?: DocumentationPreviewMeta
      analysisError?: string
      pageCount?: number
      files?: DraftMetadataFileEntry[]
      publicationResult?: { proyectoId?: string }
      analysis?: { documentInsight?: unknown }
      documentInsight?: unknown
    }
    validation?: DraftValidationShape
    project?: {
      name?: string
      description?: string
      zone?: string
      startDate?: string
      endDate?: string
    }
    subprojects?: Array<{
      tempId?: string
      detectedLabel?: string
      selectedSkillId?: number
      description?: string
      status?: string
      skillMatchKind?: ProjectAutofillSkillMatchKind
      matchedSkillName?: string
      matchScore?: number
      name?: string
      link?: string
      startDate?: string
      endDate?: string
      datesInheritedFromProject?: boolean
      recommendedQuotationWindow?: { startDate: string; endDate: string }
      sourcePages?: number[]
      documentationSlots?: DocumentationSlots
      documentationFileIds?: DocumentationFileIds
      sourceFileId?: string
      documentationSlotSources?: DocumentationSlotSources
      budgetSheetState?: BudgetSheetState
      metricSheetState?: MetricSheetState
      metricEligibility?: MetricEligibility
      quotationEligibility?: QuotationEligibility
      technicalSpecificationState?: TechnicalSpecificationState
      technicalSpecificationSuggestionStatus?: 'generated' | 'insufficient'
      technicalSpecificationSuggestionReason?: string
      technicalSpecEligibility?: TechnicalSpecEligibility
    }>
  }
}

export type SkillOption = {
  id: number
  name: string
}

export type RubroRow = {
  tempId: string
  detectedLabel: string
  selectedSkillId?: number
  description: string
  status: 'OPEN' | 'CLOSE'
  skillMatchKind?: ProjectAutofillSkillMatchKind
  matchedSkillName?: string
  matchScore?: number
  name?: string
  link?: string
  startDate?: string
  endDate?: string
  datesInheritedFromProject?: boolean
  recommendedQuotationWindow?: { startDate: string; endDate: string }
  documentationSlots?: DocumentationSlots
  sourcePages?: number[]
  documentationFileIds?: DocumentationFileIds
  sourceFileId?: string
  documentationSlotSources?: DocumentationSlotSources
  manualAdd?: boolean
  budgetSheetState?: BudgetSheetState
  metricSheetState?: MetricSheetState
  metricComputationView?: MetricComputationView
  metricLegajo?: MetricLegajo
  canImportMetricEdifySheet?: boolean
  metricEligibility?: MetricEligibility
  quotationEligibility?: QuotationEligibility
  technicalSpecificationState?: TechnicalSpecificationState
  technicalSpecificationSuggestionStatus?: 'generated' | 'insufficient'
  technicalSpecificationSuggestionReason?: string
  technicalSpecEligibility?: TechnicalSpecEligibility
}

export function removePageFromDocumentationSlotSources(
  sources: DocumentationSlotSources,
  slot: keyof DocumentationSlots,
  page: number,
  fileId?: string | null
): DocumentationSlotSources {
  const list = sources[slot]
  if (!list?.length) return { ...sources }
  const next: DocumentationSlotSources = { ...sources }
  const mapped = list
    .map((e) => {
      if (fileId && e.fileId !== fileId) return e
      return { ...e, pages: e.pages.filter((p) => p !== page) }
    })
    .filter((e) => e.pages.length > 0)
  if (mapped.length) next[slot] = mapped
  else delete next[slot]
  return next
}

export function applySlotPagesToRowDocumentation(
  row: RubroRow,
  slot: keyof DocumentationSlots,
  pages: number[] | undefined
): Pick<RubroRow, 'documentationSlots' | 'documentationSlotSources'> {
  const norm = normalizePageList(pages)
  const slots = normalizeDocumentationSlots(row.documentationSlots ?? {})
  const nextSlots = { ...slots }
  const curSrc = normalizeDocumentationSlotSources(row.documentationSlotSources ?? {})
  const nextSrc: DocumentationSlotSources = { ...curSrc }

  if (!norm?.length) {
    delete nextSlots[slot]
    delete nextSrc[slot]
  } else {
    nextSlots[slot] = [...norm]
    const prevList = curSrc[slot]
    if (prevList && prevList.length > 1) {
      const adjusted = prevList
        .map((e) => ({ fileId: e.fileId, pages: e.pages.filter((p) => norm.includes(p)) }))
        .filter((e) => e.pages.length > 0)
      if (adjusted.length) nextSrc[slot] = adjusted
      else delete nextSrc[slot]
    } else {
      const fid =
        (prevList?.[0]?.fileId && String(prevList[0].fileId).trim()) ||
        (row.sourceFileId && String(row.sourceFileId).trim()) ||
        ''
      if (fid) nextSrc[slot] = [{ fileId: fid, pages: [...norm] }]
      else delete nextSrc[slot]
    }
  }

  const hasSlots = documentationSlotsHavePages(nextSlots)
  const hasSrc = documentationSlotSourcesHaveData(nextSrc)
  return {
    documentationSlots: hasSlots ? nextSlots : {},
    documentationSlotSources: hasSrc ? nextSrc : undefined
  }
}

export function removeDocumentationChipPage(
  row: RubroRow,
  slot: keyof DocumentationSlots,
  page: number,
  fileId?: string | null
): Pick<RubroRow, 'documentationSlots' | 'documentationSlotSources'> {
  const slots = normalizeDocumentationSlots(row.documentationSlots ?? {})
  const curSrc = normalizeDocumentationSlotSources(row.documentationSlotSources ?? {})
  const hasSrc = documentationSlotSourcesHaveData(curSrc)
  if (!hasSrc) {
    const cur = normalizePageList(slots[slot]) ?? []
    return applySlotPagesToRowDocumentation(row, slot, cur.filter((p) => p !== page))
  }
  const nextSrc = removePageFromDocumentationSlotSources(curSrc, slot, page, fileId ?? undefined)
  const union = documentationSlotUnionPages(nextSrc, slot)
  const nextSlots = { ...slots }
  if (union?.length) nextSlots[slot] = union
  else delete nextSlots[slot]
  const hasSlots = documentationSlotsHavePages(nextSlots)
  const hasNextSrc = documentationSlotSourcesHaveData(nextSrc)
  return {
    documentationSlots: hasSlots ? nextSlots : {},
    documentationSlotSources: hasNextSrc ? nextSrc : undefined
  }
}

export function rubroHasValidSelectedSkill(r: RubroRow): boolean {
  const id = r.selectedSkillId
  return typeof id === 'number' && Number.isFinite(id) && id > 0
}

export function isRubroFallbackOtrosKind(kind?: ProjectAutofillSkillMatchKind | string): boolean {
  return kind === 'fallback_otros'
}

export function rubroNeedsMasterPdfForCrop(r: RubroRow): boolean {
  if (String(r.sourceFileId || '').trim()) return false
  const ids = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
  const slots = normalizeDocumentationSlots(r.documentationSlots ?? {})
  const src = normalizeDocumentationSlotSources(r.documentationSlotSources ?? {})
  return DOCUMENTATION_SLOT_KEYS.some((k) => {
    const hasFile = typeof ids[k] === 'string' && String(ids[k]).trim().length > 0
    const pages = slots[k]
    const hasPages = Array.isArray(pages) && pages.length > 0
    const hasSlotSources = (src[k]?.length ?? 0) > 0
    return hasPages && !hasFile && !hasSlotSources
  })
}

function getMaxPagesForDocumentationSource(
  fileId: string,
  files: DraftMetadataFileEntry[] | undefined,
  maxPageFallback: number
): number {
  const m = files?.find((f) => String(f.id ?? '') === String(fileId))
  const pc = m?.pageCount
  if (typeof pc === 'number' && Number.isFinite(pc) && pc >= 1) return pc
  return maxPageFallback
}

function rubroHasInvalidDocumentationPagesForRow(
  r: RubroRow,
  maxPage: number,
  files?: DraftMetadataFileEntry[]
): boolean {
  const fileIds = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
  const norm = normalizeDocumentationSlots(r.documentationSlots ?? {})
  const src = normalizeDocumentationSlotSources(r.documentationSlotSources ?? {})
  for (const k of DOCUMENTATION_SLOT_KEYS) {
    const fid = fileIds[k]
    if (typeof fid === 'string' && fid.trim().length > 0) continue
    const entries = src[k]
    if (entries?.length) {
      for (const ent of entries) {
        const lim = getMaxPagesForDocumentationSource(ent.fileId, files, maxPage)
        for (const p of ent.pages) {
          if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > lim) return true
        }
      }
      continue
    }
    const arr = norm[k]
    if (!Array.isArray(arr)) continue
    for (const p of arr) {
      if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) return true
    }
  }
  if (Array.isArray(r.sourcePages)) {
    for (const p of r.sourcePages) {
      if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) return true
    }
  }
  return false
}

export function getRubroTempIdsWithDocumentationPageIssues(
  rows: RubroRow[],
  maxPage: number,
  files?: DraftMetadataFileEntry[]
): string[] {
  if (!Number.isFinite(maxPage) || maxPage < 1) return []
  const out: string[] = []
  for (let idx = 0; idx < rows.length; idx++) {
    const r = rows[idx]
    const tid = r.tempId || `sp-${idx}`
    if (rubroHasInvalidDocumentationPagesForRow(r, maxPage, files)) out.push(tid)
  }
  return out
}

export const validateRubrosDocumentationPages = (
  rows: RubroRow[],
  maxPage: number,
  files?: DraftMetadataFileEntry[]
): string | null => {
  if (!Number.isFinite(maxPage) || maxPage < 1) return null
  for (const r of rows) {
    if (!rubroHasInvalidDocumentationPagesForRow(r, maxPage, files)) continue
    const fileIds = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
    const norm = normalizeDocumentationSlots(r.documentationSlots ?? {})
    const src = normalizeDocumentationSlotSources(r.documentationSlotSources ?? {})
    for (const k of DOCUMENTATION_SLOT_KEYS) {
      if (typeof fileIds[k] === 'string' && fileIds[k]!.trim().length > 0) continue
      const entries = src[k]
      if (entries?.length) {
        for (const ent of entries) {
          const lim = getMaxPagesForDocumentationSource(ent.fileId, files, maxPage)
          for (const p of ent.pages) {
            if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > lim) {
              return `P\u00e1gina inv\u00e1lida (${p}) en "${r.detectedLabel || r.tempId}" para el archivo vinculado: use n\u00fameros entre 1 y ${lim}.`
            }
          }
        }
        continue
      }
      const arr = norm[k]
      if (!Array.isArray(arr)) continue
      for (const p of arr) {
        if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) {
          return `P\u00e1gina inv\u00e1lida (${p}) en "${r.detectedLabel || r.tempId}": use n\u00fameros entre 1 y ${maxPage}.`
        }
      }
    }
    if (Array.isArray(r.sourcePages)) {
      for (const p of r.sourcePages) {
        if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) {
          return `P\u00e1gina inv\u00e1lida (${p}) en p\u00e1ginas relevantes de "${r.detectedLabel || r.tempId}": use n\u00fameros entre 1 y ${maxPage}.`
        }
      }
    }
  }
  return null
}

export const shallowMergeDraftMetadataForPatch = (
  metadata: ProjectAutofillPayload['draft'] extends { metadata?: infer M } ? M : never
): Record<string, unknown> => {
  const base =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata)
      ? ({ ...(metadata as Record<string, unknown>) } as Record<string, unknown>)
      : {}
  return { ...base, source: 'frontend' }
}

export const serializeRubrosSnapshot = (rows: RubroRow[]) =>
  JSON.stringify(
    [...rows]
      .sort((a, b) => a.tempId.localeCompare(b.tempId))
      .map((r) => ({
        tempId: r.tempId,
        detectedLabel: r.detectedLabel,
        matchedSkillName: r.matchedSkillName,
        selectedSkillId: r.selectedSkillId,
        manualAdd: r.manualAdd === true,
        description: r.description,
        status: r.status,
        startDate: r.startDate,
        endDate: r.endDate,
        datesInheritedFromProject: r.datesInheritedFromProject,
        documentationSlotsKey: DOCUMENTATION_SLOT_KEYS.map((k) => {
          const arr = r.documentationSlots?.[k]
          return `${String(k)}:${Array.isArray(arr) ? [...arr].sort((x, y) => x - y).join(',') : ''}`
        }).join(';'),
        documentationFileIdsKey: DOCUMENTATION_SLOT_KEYS.map((k) => {
          const id = r.documentationFileIds?.[k]
          return `${String(k)}:${id != null ? String(id) : ''}`
        }).join(';'),
        sourceFileIdKey: String(r.sourceFileId || '').trim(),
        documentationSlotSourcesKey: DOCUMENTATION_SLOT_KEYS.map((k) => {
          const list = normalizeDocumentationSlotSources(r.documentationSlotSources ?? {})[k]
          if (!list?.length) return `${String(k)}:`
          return `${String(k)}:${list
            .map((e) => `${e.fileId}@${(e.pages || []).slice().sort((x, y) => x - y).join(',')}`)
            .join('|')}`
        }).join(';'),
        sourcePagesKey: (r.sourcePages || []).slice().sort((x, y) => x - y).join(','),
        budgetSheetKey: serializeBudgetSheetForDirtySnapshot(r.budgetSheetState),
        metricSheetKey: serializeMetricSheetForDirtySnapshot(r.metricSheetState),
        technicalSpecKey: serializeTechnicalSpecForDirtySnapshot(r.technicalSpecificationState)
      }))
  )

export type CommittedDraftSnapshot = {
  name: string
  description: string
  zone: string
  startDate: string
  endDate: string
  rubrosKey: string
  rubrosCount: number
}

export const mergeValidationWarnings = (a?: string[], b?: string[]) => {
  const out: string[] = []
  const seen = new Set<string>()
  for (const list of [a, b]) {
    for (const raw of list || []) {
      const w = String(raw || '').trim()
      if (!w || seen.has(w)) continue
      seen.add(w)
      out.push(w)
    }
  }
  return out
}

export const EMPTY_REQUIRED_FIELDS: string[] = []

export const READINESS_MISSING_LABELS: Record<string, string> = {
  'project.name': 'Nombre del proyecto',
  'project.description': 'Descripcion del proyecto',
  'project.startDate': 'Fecha de inicio del proyecto',
  'project.endDate': 'Fecha de finalizacion del proyecto',
  'project.zone': 'Ubicacion de la obra',
  subprojects: 'Rubros a cotizar',
  'subproject.selectedSkillId': 'Rubro con skill elegido',
  'subproject.detectedLabel': 'Etiqueta del rubro',
  dateConstraintOk: 'Fechas coherentes (inicio y fin)'
}

export const formatReadinessKeys = (keys?: string[]) =>
  (keys || []).map((k) => READINESS_MISSING_LABELS[k] || k.replace(/_/g, ' '))

export const humanizeDraftStatus = (status?: string) => {
  const normalized = String(status || '').trim().toLowerCase()
  const map: Record<string, string> = {
    draft: 'Borrador',
    in_review: 'En revision',
    inreview: 'En revision',
    ready: 'Listo',
    completed: 'Completado'
  }
  if (map[normalized]) return map[normalized]
  if (!normalized) return 'Borrador'
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

export const clampIsoDateToMax = (d: string, maxStr: string) => {
  const nd = toHtmlDateInputValue(d)
  const nmax = toHtmlDateInputValue(maxStr)
  if (!nmax) return nd
  if (!nd) return ''
  return nd > nmax ? nmax : nd
}

export type MapRubrosToDraftPayloadOptions = {
  omitBudgetSheetTempIds?: ReadonlySet<string>
}

export const mapRubrosToDraftPayload = (
  rows: RubroRow[],
  resolveSkillName?: (skillId: number) => string | undefined,
  options?: MapRubrosToDraftPayloadOptions
): DraftSubprojectPayload[] =>
  rows.map((r) => {
    const omitBudgetSheet = options?.omitBudgetSheetTempIds?.has(r.tempId) === true
    const row: DraftSubprojectPayload = {
      tempId: r.tempId,
      detectedLabel: r.detectedLabel || undefined,
      description: r.description || undefined,
      status: r.status === 'CLOSE' ? 'CLOSE' : 'OPEN',
      startDate: toHtmlDateInputValue(r.startDate) || undefined,
      endDate: toHtmlDateInputValue(r.endDate) || undefined
    }
    if (rubroHasValidSelectedSkill(r)) {
      row.selectedSkillId = r.selectedSkillId
    }
    const sid = rubroHasValidSelectedSkill(r) ? r.selectedSkillId : undefined
    const fromCatalog =
      typeof sid === 'number' ? String(resolveSkillName?.(sid) || '').trim() : ''
    const fromRow = String(r.matchedSkillName || '').trim()
    const realSkillName = fromCatalog || fromRow
    if (realSkillName) {
      row.matchedSkillName = realSkillName
    }
    if (r.datesInheritedFromProject === true || r.datesInheritedFromProject === false) {
      row.datesInheritedFromProject = r.datesInheritedFromProject
    }
    const norm = normalizeDocumentationSlots(r.documentationSlots ?? {})
    const slotsOut: NonNullable<DraftSubprojectPayload['documentationSlots']> = {}
    for (const k of DOCUMENTATION_SLOT_KEYS) {
      const arr = norm[k]
      if (Array.isArray(arr) && arr.length > 0) {
        slotsOut[k] = [...arr]
      }
    }
    row.documentationSlots = slotsOut
    if (r.sourcePages && r.sourcePages.length > 0) {
      row.sourcePages = [...r.sourcePages]
    }
    const idsNorm = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
    const idsOut: NonNullable<DraftSubprojectPayload['documentationFileIds']> = {}
    for (const k of DOCUMENTATION_SLOT_KEYS) {
      const id = idsNorm[k]
      if (typeof id === 'string' && id.length > 0) {
        idsOut[k] = id
      }
    }
    if (Object.keys(idsOut).length > 0) {
      row.documentationFileIds = idsOut
    }
    const srcNorm = normalizeDocumentationSlotSources(r.documentationSlotSources ?? {})
    if (documentationSlotSourcesHaveData(srcNorm)) {
      const srcOut: NonNullable<DraftSubprojectPayload['documentationSlotSources']> = {}
      for (const k of DOCUMENTATION_SLOT_KEYS) {
        const list = srcNorm[k]
        if (!list?.length) continue
        srcOut[k] = list.map((e) => ({ fileId: e.fileId, pages: [...e.pages] }))
      }
      if (Object.keys(srcOut).length > 0) {
        row.documentationSlotSources = srcOut
      }
    }
    const sfi = r.sourceFileId != null ? String(r.sourceFileId).trim() : ''
    if (sfi) row.sourceFileId = sfi
    if (r.metricSheetState && typeof r.metricSheetState === 'object') {
      const clonedMetric = JSON.parse(JSON.stringify(r.metricSheetState)) as MetricSheetState
      row.metricSheetState = attachMetricBindingToState(
        {
          tempId: r.tempId,
          detectedLabel: r.detectedLabel,
          selectedSkillId: r.selectedSkillId
        },
        clonedMetric
      )
    }
    if (
      !omitBudgetSheet &&
      r.manualAdd !== true &&
      r.budgetSheetState &&
      typeof r.budgetSheetState === 'object' &&
      rubroMetricReadyFromRow(r)
    ) {
      const cloned = JSON.parse(JSON.stringify(r.budgetSheetState)) as BudgetSheetState
      row.budgetSheetState = attachBudgetBindingToState(
        {
          tempId: r.tempId,
          detectedLabel: r.detectedLabel,
          selectedSkillId: r.selectedSkillId
        },
        cloned
      )
    }
    if (
      !rubroTechnicalSpecLegajoInPdf(r) &&
      r.technicalSpecificationState &&
      typeof r.technicalSpecificationState === 'object'
    ) {
      const clonedSpec = JSON.parse(
        JSON.stringify(r.technicalSpecificationState)
      ) as TechnicalSpecificationState
      row.technicalSpecificationState = attachTechnicalSpecBindingToState(
        {
          tempId: r.tempId,
          detectedLabel: r.detectedLabel,
          selectedSkillId: r.selectedSkillId
        },
        clonedSpec
      )
    }
    return row
  })

export function extractDocumentInsightFromDraftMetadata(metadata: unknown): DocumentInsight | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null
  }
  const m = metadata as Record<string, unknown>
  const analysis =
    m.analysis && typeof m.analysis === 'object' && !Array.isArray(m.analysis)
      ? (m.analysis as Record<string, unknown>)
      : null
  const fromAnalysis = analysis?.documentInsight
  const fromTop = m.documentInsight
  return (
    parseDocumentInsight(fromAnalysis) ??
    parseDocumentInsight(fromTop) ??
    null
  )
}

export function extractDraftSubprojectsFromApiResponse(
  res: unknown
): NonNullable<ProjectAutofillPayload['draft']>['subprojects'] | null {
  if (!res || typeof res !== 'object') return null
  const raw = res as Record<string, unknown>
  const inner =
    raw.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : raw
  const nestedDraft =
    inner.draft && typeof inner.draft === 'object'
      ? (inner.draft as Record<string, unknown>)
      : null
  const source = nestedDraft ?? inner
  const subs = source.subprojects
  if (!Array.isArray(subs)) return null
  return subs as NonNullable<ProjectAutofillPayload['draft']>['subprojects']
}

export function mergeRubroSheetStatesFromDraftSubprojects(
  rubros: RubroRow[],
  subprojects:
    | NonNullable<ProjectAutofillPayload['draft']>['subprojects']
    | null
    | undefined,
  tempIds: ReadonlySet<string>
): RubroRow[] {
  if (!subprojects?.length || tempIds.size === 0) return rubros
  return rubros.map((r) => {
    if (!tempIds.has(r.tempId)) return r
    const sp = subprojects.find((x) => String(x?.tempId ?? '') === r.tempId)
    if (!sp || typeof sp !== 'object') return r
    const spRec = sp as Record<string, unknown>
    const next: RubroRow = { ...r }
    if (
      spRec.budgetSheetState != null &&
      typeof spRec.budgetSheetState === 'object' &&
      !Array.isArray(spRec.budgetSheetState)
    ) {
      next.budgetSheetState = spRec.budgetSheetState as BudgetSheetState
    }
    if (
      spRec.metricSheetState != null &&
      typeof spRec.metricSheetState === 'object' &&
      !Array.isArray(spRec.metricSheetState)
    ) {
      next.metricSheetState = spRec.metricSheetState as MetricSheetState
    }
    const workflow = extractRubroMetricWorkflowFields(spRec)
    if (workflow.metricComputationView) {
      next.metricComputationView = workflow.metricComputationView
      if (workflow.metricComputationView !== 'legajo') {
        next.metricLegajo = undefined
      }
    }
    if (workflow.metricLegajo) next.metricLegajo = workflow.metricLegajo
    next.canImportMetricEdifySheet = workflow.canImportMetricEdifySheet ? true : undefined
    const me = parseMetricEligibility(spRec.metricEligibility)
    if (me) next.metricEligibility = me
    const qe = parseQuotationEligibility(spRec.quotationEligibility)
    if (qe) next.quotationEligibility = qe
    return next
  })
}

export const validationBundleFromPatchResponse = (
  res: unknown
):
  | {
      validation?: DraftValidationShape
      collectedData?: ProjectAutofillPayload['collectedData']
    }
  | null => {
  if (!res || typeof res !== 'object') return null
  const raw = res as Record<string, unknown>
  const inner =
    raw.data && typeof raw.data === 'object'
      ? (raw.data as Record<string, unknown>)
      : raw
  const nestedDraft =
    inner.draft && typeof inner.draft === 'object'
      ? (inner.draft as Record<string, unknown>)
      : null

  const v =
    (inner.validation as DraftValidationShape | undefined) ??
    (nestedDraft?.validation as DraftValidationShape | undefined)
  let cd =
    (inner.collectedData as ProjectAutofillPayload['collectedData'] | undefined) ??
    (nestedDraft?.collectedData as ProjectAutofillPayload['collectedData'] | undefined)

  const statusVal = inner.status ?? nestedDraft?.status
  if (typeof statusVal === 'string' || typeof statusVal === 'number') {
    cd = {
      ...(cd && typeof cd === 'object' ? cd : {}),
      draftStatus: String(statusVal)
    }
  }

  const hasV = v != null && typeof v === 'object'
  const hasCd = cd != null && typeof cd === 'object' && Object.keys(cd).length > 0
  if (!hasV && !hasCd) return null

  return {
    validation: hasV ? v : undefined,
    collectedData: hasCd ? cd : undefined
  }
}

export function projectAutofillDraftApiToCardPayload(
  raw: unknown,
  fallbackDraftId: string
): ProjectAutofillPayload {
  const baseDraft: NonNullable<ProjectAutofillPayload['draft']> = {
    id: fallbackDraftId
  }
  const empty: ProjectAutofillPayload = {
    actionId: 'project-autofill',
    draftId: fallbackDraftId,
    draft: baseDraft
  }
  if (!raw || typeof raw !== 'object') return empty
  const r = raw as Record<string, unknown>
  const inner =
    r.data && typeof r.data === 'object' && !Array.isArray(r.data)
      ? (r.data as Record<string, unknown>)
      : r

  const nestedDraft =
    inner.draft && typeof inner.draft === 'object'
      ? (inner.draft as Record<string, unknown>)
      : null

  const source = nestedDraft ?? inner

  const idCandidate = source.id ?? inner.id ?? fallbackDraftId
  const draftId =
    idCandidate != null && String(idCandidate).trim() !== ''
      ? String(idCandidate).trim()
      : fallbackDraftId

  const validationFromSource =
    (source.validation as DraftValidationShape | undefined) ||
    (inner.validation as DraftValidationShape | undefined)

  let collectedData =
    (inner.collectedData as ProjectAutofillPayload['collectedData']) ??
    (source.collectedData as ProjectAutofillPayload['collectedData'])

  const statusVal = source.status ?? inner.status
  if (typeof statusVal === 'string' || typeof statusVal === 'number') {
    collectedData = {
      ...(collectedData && typeof collectedData === 'object' ? collectedData : {}),
      draftId,
      draftStatus: String(statusVal)
    }
  }

  const rawProject =
    source.project && typeof source.project === 'object' && !Array.isArray(source.project)
      ? (source.project as NonNullable<ProjectAutofillPayload['draft']>['project'] &
          Record<string, unknown>)
      : null
  const normalizedProject =
    rawProject != null
      ? ({
          ...rawProject,
          startDate: toHtmlDateInputValue(rawProject.startDate) || undefined,
          endDate: toHtmlDateInputValue(rawProject.endDate) || undefined
        } as NonNullable<ProjectAutofillPayload['draft']>['project'])
      : undefined

  const rawSubs = Array.isArray(source.subprojects) ? source.subprojects : null
  const normalizedSubprojects = rawSubs
    ? (rawSubs.map((sp) => {
        const row = sp as Record<string, unknown> & {
          startDate?: string
          endDate?: string
        }
        return {
          ...row,
          startDate: toHtmlDateInputValue(row.startDate) || undefined,
          endDate: toHtmlDateInputValue(row.endDate) || undefined
        } as NonNullable<NonNullable<ProjectAutofillPayload['draft']>['subprojects']>[number]
      }) as NonNullable<NonNullable<ProjectAutofillPayload['draft']>['subprojects']>)
    : undefined

  const draft: NonNullable<ProjectAutofillPayload['draft']> = {
    id: draftId,
    status: typeof source.status === 'string' ? source.status : undefined,
    metadata:
      source.metadata && typeof source.metadata === 'object' && !Array.isArray(source.metadata)
        ? (source.metadata as NonNullable<ProjectAutofillPayload['draft']>['metadata'])
        : undefined,
    validation:
      source.validation && typeof source.validation === 'object'
        ? (source.validation as DraftValidationShape)
        : undefined,
    project: normalizedProject,
    subprojects: normalizedSubprojects
  }

  const documentInsight = extractDocumentInsightFromDraftMetadata(draft.metadata)

  return {
    actionId: 'project-autofill',
    draftId,
    validation: validationFromSource,
    collectedData: collectedData ?? undefined,
    draft,
    ...(documentInsight ? { documentInsight } : {})
  }
}

export const parseProjectAutofill = (llmRawResponse?: string): ProjectAutofillPayload | null => {
  if (!llmRawResponse) return null
  try {
    const parsed = JSON.parse(llmRawResponse) as ProjectAutofillPayload
    const isProjectAutofill =
      parsed.actionId === 'project-autofill' || isProjectAutofillAgentId(parsed.activeAgent)
    if (!isProjectAutofill) return null
    const hasDraftId = !!(parsed.draftId || parsed.collectedData?.draftId || parsed.draft?.id)
    const hasDraftContent = !!parsed.draft
    if (!hasDraftId && !hasDraftContent) return null
    const fromMeta = extractDocumentInsightFromDraftMetadata(parsed.draft?.metadata)
    const documentInsight = parsed.documentInsight ?? fromMeta
    const base = documentInsight ? { ...parsed, documentInsight } : parsed
    const d = base.draft
    if (!d) return base
    const proj = d.project
    const normProj =
      proj && typeof proj === 'object' && !Array.isArray(proj)
        ? ({
            ...proj,
            startDate: toHtmlDateInputValue((proj as { startDate?: string }).startDate) || undefined,
            endDate: toHtmlDateInputValue((proj as { endDate?: string }).endDate) || undefined
          } as NonNullable<ProjectAutofillPayload['draft']>['project'])
        : proj
    const subs = d.subprojects
    const normSubs = Array.isArray(subs)
      ? subs.map((sp) => ({
          ...sp,
          startDate: toHtmlDateInputValue(sp.startDate) || undefined,
          endDate: toHtmlDateInputValue(sp.endDate) || undefined
        }))
      : subs
    return { ...base, draft: { ...d, project: normProj, subprojects: normSubs } }
  } catch {
    return null
  }
}
